/**
 * Context Helpers
 *
 * Pure functions for working with the multi-context user model.
 * These are used by auth store selectors and guards.
 *
 * Includes a bridge function (buildContextsFromLegacyRole) that derives
 * contexts from the old single-role model during migration.
 */

import type { User, UserRole } from '../types/user.types'
import type { AdminRole } from '../types/admin.types'
import type {
  ContextType,
  UserContexts,
  SchoolContext,
  BusinessContext,
  PlatformContext,
  SchoolRole,
  PlatformRole,
  BusinessRole,
} from '../types/context.types'

// ============================================================================
// Bridge: Legacy role → Contexts
// ============================================================================

/**
 * Build contexts from legacy role field during migration.
 * This ensures old User objects (from seed data, localStorage, etc.)
 * get proper contexts populated.
 */
export function buildContextsFromLegacyRole(user: User): UserContexts {
  // If user already has contexts, return them
  if (user.contexts) return user.contexts

  const contexts: UserContexts = {
    personal: { type: 'personal' },
  }

  switch (user.role) {
    case 'student':
      if (user.schoolId) {
        contexts.school = {
          type: 'school',
          role: 'student',
          schoolId: user.schoolId,
          tier: user.accountTier === 'tier_1_school_only'
            ? 'tier_1_school_only'
            : user.accountTier === 'tier_2_full'
              ? 'tier_2_full'
              : undefined,
        }
      }
      break

    case 'teacher':
      if (user.schoolId) {
        contexts.school = {
          type: 'school',
          role: 'teacher',
          schoolId: user.schoolId,
        }
      }
      break

    case 'school_admin':
      if (user.schoolId) {
        contexts.school = {
          type: 'school',
          role: 'school_admin',
          schoolId: user.schoolId,
        }
      }
      break

    case 'head_school_admin':
      if (user.schoolId) {
        contexts.school = {
          type: 'school',
          role: 'head_school_admin',
          schoolId: user.schoolId,
        }
      }
      break

    case 'vendor':
      contexts.business = {
        type: 'business',
        role: 'owner',
        businessId: user.vendorProfileId ?? user.id,
      }
      break

    case 'staff':
      contexts.platform = {
        type: 'platform',
        role: 'staff',
      }
      break

    case 'platform_admin':
      contexts.platform = {
        type: 'platform',
        role: 'admin',
        adminSubRole: 'super' as AdminRole, // Default during migration
      }
      break

    // 'user' and 'parent' get personal context only
    // Parent is identified by linkedChildIds, not a context
    default:
      break
  }

  return contexts
}

/**
 * Derive the default active context from a user's contexts.
 * Prioritizes non-personal contexts if available.
 */
export function getDefaultActiveContext(user: User): ContextType {
  if (user.activeContext) return user.activeContext

  const contexts = user.contexts ?? buildContextsFromLegacyRole(user)

  // Students stay in personal context — school is just a community for them
  if (contexts.school?.role === 'student') return 'personal'
  // Other school roles (admin, teacher) default to school context
  if (contexts.school) return 'school'
  // If user has a business context, default to business
  if (contexts.business) return 'business'
  // If user has a platform context, default to platform
  if (contexts.platform) return 'platform'

  return 'personal'
}

// ============================================================================
// Context queries
// ============================================================================

/**
 * Get the user's contexts, building from legacy role if needed.
 */
function getContexts(user: User): UserContexts {
  return user.contexts ?? buildContextsFromLegacyRole(user)
}

/**
 * Does the user have a given context type?
 */
export function hasContext(user: User, context: ContextType): boolean {
  const contexts = getContexts(user)
  switch (context) {
    case 'personal':
      return true // Everyone always has personal
    case 'school':
      return !!contexts.school
    case 'business':
      return !!contexts.business
    case 'platform':
      return !!contexts.platform
  }
}

/**
 * Get the user's role within a given context, or null.
 */
export function getRoleInContext(
  user: User,
  context: ContextType
): SchoolRole | BusinessRole | PlatformRole | null {
  const contexts = getContexts(user)
  switch (context) {
    case 'personal':
      return null // Personal context has no sub-role
    case 'school':
      return contexts.school?.role ?? null
    case 'business':
      return contexts.business?.role ?? null
    case 'platform':
      return contexts.platform?.role ?? null
  }
}

/**
 * Get all context types the user has.
 */
export function getAvailableContexts(user: User): ContextType[] {
  const contexts = getContexts(user)
  const available: ContextType[] = ['personal']

  if (contexts.school) available.push('school')
  if (contexts.business) available.push('business')
  if (contexts.platform) available.push('platform')

  return available
}

// ============================================================================
// Convenience checks
// ============================================================================

/**
 * Is the user a school admin (school_admin or head_school_admin)?
 */
export function isSchoolAdmin(user: User): boolean {
  const role = getContexts(user).school?.role
  return role === 'school_admin' || role === 'head_school_admin'
}

/**
 * Is the user a head school admin?
 */
export function isHeadSchoolAdmin(user: User): boolean {
  return getContexts(user).school?.role === 'head_school_admin'
}

/**
 * Does the user have any school context role?
 */
export function isSchoolStaff(user: User): boolean {
  return !!getContexts(user).school
}

/**
 * Is the user a platform admin?
 */
export function isPlatformAdmin(user: User): boolean {
  return getContexts(user).platform?.role === 'admin'
}

/**
 * Does the user have any platform context role (staff or admin)?
 */
export function isPlatformStaff(user: User): boolean {
  return !!getContexts(user).platform
}

/**
 * Can the user verify track records? (platform staff or admin)
 */
export function canVerify(user: User): boolean {
  return isPlatformStaff(user)
}

/**
 * Does the user have a business context (is a vendor)?
 */
export function isVendor(user: User): boolean {
  return !!getContexts(user).business
}

/**
 * Does the user have linked children (is a parent)?
 * Parent is a person-level relationship, not a context.
 */
export function hasLinkedChildren(user: User): boolean {
  return (user.linkedChildIds?.length ?? 0) > 0
}

/**
 * Get the admin sub-role (super, content, support, etc.) or null.
 */
export function getAdminSubRole(user: User): AdminRole | null {
  return getContexts(user).platform?.adminSubRole ?? null
}

/**
 * Get the school context details, or null.
 */
export function getSchoolContext(user: User): SchoolContext | null {
  return getContexts(user).school ?? null
}

/**
 * Get the business context details, or null.
 */
export function getBusinessContext(user: User): BusinessContext | null {
  return getContexts(user).business ?? null
}

/**
 * Get the platform context details, or null.
 */
export function getPlatformContext(user: User): PlatformContext | null {
  return getContexts(user).platform ?? null
}

/**
 * Check if a user has a specific role within a specific context.
 * Used by ContextGuard.
 */
export function hasRoleInContext(
  user: User,
  context: ContextType,
  roles: string[]
): boolean {
  const role = getRoleInContext(user, context)
  if (!role) return roles.length === 0 // personal context has no role — pass if no roles required
  return roles.includes(role)
}

/**
 * Is the user a Tier 1 school-only account?
 */
export function isTier1(user: User): boolean {
  // Check school context tier first (new model)
  const schoolCtx = getContexts(user).school
  if (schoolCtx?.tier === 'tier_1_school_only') return true
  // Fallback to legacy field
  return user.accountTier === 'tier_1_school_only'
}

// ============================================================================
// Legacy compatibility
// ============================================================================

/**
 * Map of old UserRole values to the context check they correspond to.
 * Used by the bridge layer so old RoleGuard usages still work.
 */
export function legacyRoleMatchesContexts(
  user: User,
  roles: UserRole[]
): boolean {
  for (const role of roles) {
    switch (role) {
      case 'user':
        // Everyone is a user
        return true
      case 'student':
        if (getContexts(user).school?.role === 'student') return true
        break
      case 'teacher':
        if (getContexts(user).school?.role === 'teacher') return true
        break
      case 'school_admin':
        if (getContexts(user).school?.role === 'school_admin') return true
        break
      case 'head_school_admin':
        if (getContexts(user).school?.role === 'head_school_admin') return true
        break
      case 'parent':
        if (hasLinkedChildren(user)) return true
        break
      case 'vendor':
        if (isVendor(user)) return true
        break
      case 'staff':
        if (getContexts(user).platform?.role === 'staff') return true
        break
      case 'platform_admin':
        if (isPlatformAdmin(user)) return true
        break
    }
  }
  return false
}

// ── Context Home Routes ──────────────────────────────────────────────

const CONTEXT_HOME_ROUTES: Record<ContextType, string> = {
  personal: '/',
  school: '/school',
  business: '/vendor',
  platform: '/admin',
}

export function getContextHomeRoute(context: ContextType, user?: User): string {
  if (context === 'school' && user) {
    const role = getRoleInContext(user, 'school')
    if (role === 'student') return '/school/student-dashboard'
  }
  return CONTEXT_HOME_ROUTES[context]
}
